import React, { useEffect, useState } from 'react';
import { Modal } from '../../../components/organisms/Modal';
import { Button } from '../../../components/atoms';
import { FormField } from '../../../components/molecues/FormField';
import { getArtistService, getAlbumService } from '../../../service/ServiceRegistry';
import type {
  Song,
  TrackAccessLevel,
  TrackStatus,
  CreateTrackRequest,
  UpdateTrackRequest,
} from '../../../types/api/song';

interface SongFormState {
  title: string;
  artistId: string;
  albumId: string;
  trackNumber: string;
  durationSeconds: string;
  accessLevel: TrackAccessLevel;
  status: TrackStatus;
}

interface Option {
  id: string;
  label: string;
}

const defaultFormState: SongFormState = {
  title: '',
  artistId: '',
  albumId: '',
  trackNumber: '',
  durationSeconds: '',
  accessLevel: 'FREE',
  status: 'ACTIVE',
};

export interface SongFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateTrackRequest | UpdateTrackRequest,
    audioFile?: File | null
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  song?: Song;
}

export const SongFormModal: React.FC<SongFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  song,
}) => {
  const [formData, setFormData] = useState<SongFormState>(defaultFormState);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioName, setAudioName] = useState<string | null>(null);
  const [artistOptions, setArtistOptions] = useState<Option[]>([]);
  const [albumOptions, setAlbumOptions] = useState<Option[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load artist + album options for the dropdowns whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadOptions = async () => {
      try {
        const [artists, albums] = await Promise.all([
          getArtistService().getArtists({ page: 1, size: 200 }),
          getAlbumService().getAlbums({ page: 1, size: 200 }),
        ]);
        if (cancelled) return;
        setArtistOptions(artists.items.map((a) => ({ id: a.artistId, label: a.name })));
        setAlbumOptions(albums.items.map((a) => ({ id: a.albumId, label: a.name })));
      } catch {
        if (!cancelled) {
          setArtistOptions([]);
          setAlbumOptions([]);
        }
      }
    };

    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (mode === 'edit' && song && isOpen) {
      setFormData({
        title: song.title || '',
        artistId: song.artistId || '',
        albumId: song.albumId || '',
        trackNumber:
          song.trackNumber !== undefined && song.trackNumber !== null
            ? String(song.trackNumber)
            : '',
        durationSeconds:
          song.durationSeconds !== undefined && song.durationSeconds !== null
            ? String(song.durationSeconds)
            : '',
        accessLevel: song.accessLevel || 'FREE',
        status: song.status || 'ACTIVE',
      });
      setAudioFile(null);
      setAudioName(null);
    } else if (isOpen) {
      setFormData(defaultFormState);
      setAudioFile(null);
      setAudioName(null);
    }
    setErrors({});
  }, [song, mode, isOpen]);

  const handleAudioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isAudio = file.type.startsWith('audio/') || /\.mp3$/i.test(file.name);
    if (!isAudio) {
      setErrors((prev) => ({ ...prev, audio: 'Please select a valid audio file (MP3)' }));
      return;
    }
    const maxSizeMb = 50;
    if (file.size > maxSizeMb * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, audio: `Audio size must be less than ${maxSizeMb}MB` }));
      return;
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.audio;
      return next;
    });
    setAudioFile(file);
    setAudioName(file.name);
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      nextErrors.title = 'Title is required';
    } else if (formData.title.trim().length > 200) {
      nextErrors.title = 'Title must be less than 200 characters';
    }
    if (!formData.artistId) {
      nextErrors.artistId = 'Artist is required';
    }
    if (formData.trackNumber.trim() !== '') {
      const n = Number(formData.trackNumber);
      if (!Number.isInteger(n) || n < 1) nextErrors.trackNumber = 'Track number must be a positive whole number';
    }
    if (formData.durationSeconds.trim() !== '') {
      const n = Number(formData.durationSeconds);
      if (!Number.isInteger(n) || n < 0) nextErrors.durationSeconds = 'Duration must be a whole number of seconds';
    }
    if (mode === 'create' && !audioFile) {
      nextErrors.audio = 'An MP3 file is required';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    const base = {
      title: formData.title.trim(),
      artistId: formData.artistId,
      albumId: formData.albumId || undefined,
      trackNumber: formData.trackNumber.trim() !== '' ? Number(formData.trackNumber) : undefined,
      durationSeconds:
        formData.durationSeconds.trim() !== '' ? Number(formData.durationSeconds) : undefined,
      accessLevel: formData.accessLevel,
      status: formData.status,
    };

    const payload: CreateTrackRequest | UpdateTrackRequest =
      mode === 'create' ? (base as CreateTrackRequest) : (base as UpdateTrackRequest);

    setIsSubmitting(true);
    try {
      await onSubmit(payload, audioFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Add Track' : 'Edit Track'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={isSubmitting} type="submit">
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          error={errors.title}
          required
          placeholder="e.g., Song Title"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
            Artist <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.artistId}
            onChange={(e) => setFormData((prev) => ({ ...prev, artistId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
          >
            <option value="">Select an artist</option>
            {artistOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {errors.artistId ? <p className="text-red-500 text-xs mt-1">{errors.artistId}</p> : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Album</label>
          <select
            value={formData.albumId}
            onChange={(e) => setFormData((prev) => ({ ...prev, albumId: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
          >
            <option value="">No album</option>
            {albumOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Track Number"
            type="number"
            value={formData.trackNumber}
            onChange={(e) => setFormData((prev) => ({ ...prev, trackNumber: e.target.value }))}
            error={errors.trackNumber}
            min={1}
            step={1}
            placeholder="e.g., 1"
          />
          <FormField
            label="Duration (seconds)"
            type="number"
            value={formData.durationSeconds}
            onChange={(e) => setFormData((prev) => ({ ...prev, durationSeconds: e.target.value }))}
            error={errors.durationSeconds}
            min={0}
            step={1}
            placeholder="e.g., 240"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Access Level</label>
            <select
              value={formData.accessLevel}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, accessLevel: e.target.value as TrackAccessLevel }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              <option value="FREE">Free</option>
              <option value="PREMIUM">Premium</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Status</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, status: e.target.value as TrackStatus }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30"
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="PENDING">Pending</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5 text-left">
            MP3 File {mode === 'create' ? <span className="text-red-500">*</span> : null}
          </label>
          <input
            type="file"
            accept="audio/*,.mp3"
            onChange={handleAudioChange}
            className="block text-sm text-gray-700"
          />
          {audioName ? (
            <p className="text-xs text-gray-500 mt-1">Selected: {audioName}</p>
          ) : mode === 'edit' ? (
            <p className="text-xs text-gray-500 mt-1">Leave empty to keep the current audio.</p>
          ) : null}
          {errors.audio ? <p className="text-sm text-red-600 mt-1">{errors.audio}</p> : null}
        </div>
      </form>
    </Modal>
  );
};

export default SongFormModal;
